import { HttpErrorResponse } from '@angular/common/http';
import { AfterContentInit, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Params } from '@angular/router';
import { ApplicationType, PollyMessageService } from 'polly-commons';
import { combineLatest } from 'rxjs';
import { SubSink } from 'subsink';
import { PollyManageAnalysisService } from '../../../../@core/services';
import { extractErrorDetails, insertAfterFirstWord } from '../../../../@core/utilities/polly-manage.utilities';
import { Bookmark, BookmarkResponse, ComputeAnalysisByIdResponse, ComputeApplicationsResponse, VersionOption } from '../../../../polly-manage.interfaces';
import { TabCloseService } from '../../services';

@Component({
  selector: 'app-polly-manage-application-desktop-page',
  templateUrl: './polly-manage-application-desktop-page.component.html',
  styleUrls: ['./polly-manage-application-desktop-page.component.scss'],
})
export class PollyManageApplicationDesktopPageComponent implements OnInit, AfterContentInit, OnDestroy {
  public appHeader!: string;
  public appUrl!: string;
  public iframeLoadError = false;
  public isShinyApp: boolean = false;
  public originalAppUrl!: string;

  private sessionId!: string;
  private workspaceId!: string;
  private analysisId!: string;
  private appId!: string;
  private readonly subSink: SubSink = new SubSink();
  public versionNames: VersionOption[] = [];

  public selectedVersion: VersionOption | null = null;
  private readonly messageService = inject(PollyMessageService);

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly titleService: Title,
    private readonly analysisService: PollyManageAnalysisService,
    private readonly tabCloseService: TabCloseService,
  ) {}

  public ngOnInit(): void {
    this.handleRouteParamsAndQueryParams();
    this.getBookmarkVersionOptions();
  }

  public ngAfterContentInit(): void {
    this.tabCloseService.initSession(this.workspaceId, this.analysisId, this.sessionId);
  }

  private handleRouteParamsAndQueryParams(): void {
    this.subSink.sink = combineLatest([this.activatedRoute.params, this.activatedRoute.queryParams]).subscribe({
      next: ([params, queryParams]: [Params, Params]) => {
        this.handleRouteParams(params);
        this.extractQueryParamsAndInitialize(queryParams);
      },
      error: (error: Error) => {
        const errorDetail = extractErrorDetails(error, 'Routing Error', 'Something went wrong while reading route parameters');
        this.showCustomToast(errorDetail.title, errorDetail.detail);
      },
    });
  }

  private handleRouteParams(params: Params): void {
    if (params && params['sessionId'] && params['appUrl']) {
      this.sessionId = params['sessionId'];
      this.originalAppUrl = decodeURIComponent(params['appUrl']);
      this.appUrl = `https://${this.originalAppUrl}`;
    } else {
      this.showCustomToast('Missing Route Parameters', 'Required route parameters are missing or invalid');
    }
  }

  private getBookmarkVersionOptions(): void {
    if (!this.workspaceId || !this.analysisId) {
      this.selectedVersion = null;
      this.messageService.showToast('Failure', 'Required parameters are missing', 'error');
      return;
    }

    this.subSink.sink = this.analysisService.getVersionDetails(this.workspaceId, this.analysisId).subscribe({
      next: (response: BookmarkResponse) => {
        if (!response?.data || !response.data.length) {
          this.versionNames = [];
        } else {
          this.versionNames = this.mapBookmarksToVersionOptions(response.data);
        }
      },
      error: (error: HttpErrorResponse) => {
        const errorDetail = extractErrorDetails(error, 'Failure', 'Something went wrong while fetching bookmark details');
        this.showCustomToast(errorDetail.title, errorDetail.detail);
      },
    });
  }

  private mapBookmarksToVersionOptions(bookmarks: Bookmark[]): VersionOption[] {
    return bookmarks.map((version: Bookmark) => ({
      viewValue: `${version.attributes.bookmark_name}`,
      value: version.attributes.bookmark_info.storageId,
    }));
  }

  private setLatestRestoreVersion(storageId: string): void {
    this.appUrl = `https://${this.originalAppUrl}&_state_id_=${storageId}`;
  }

  private extractQueryParamsAndInitialize(queryParams: Params): void {
    if (queryParams && queryParams['workspaceId'] && queryParams['analysis_id'] && queryParams['app_id']) {
      this.workspaceId = queryParams['workspaceId'];
      this.analysisId = queryParams['analysis_id'];
      this.appId = queryParams['app_id'];

      this.getApplicationInfo();
    } else {
      this.showCustomToast('Missing Parameters', 'Required query parameters are missing');
    }
  }

  public onIframeLoad(): void {
    this.iframeLoadError = false;
  }

  public onIframeError(): void {
    this.iframeLoadError = true;
  }

  public onValueChange(changeObj: VersionOption): void {
    this.selectedVersion = changeObj;
    this.setLatestRestoreVersion(changeObj.value);
  }

  private getApplicationInfo(): void {
    this.subSink.sink = this.analysisService.fetchAppAndAnalysisDetails(this.appId, this.workspaceId, this.analysisId).subscribe({
      next: ({
        appResponse,
        analysisResponse,
      }: {
        appResponse: ComputeApplicationsResponse;
        analysisResponse: ComputeAnalysisByIdResponse;
      }) => {
        if (analysisResponse && analysisResponse.data && analysisResponse.data.attributes && analysisResponse.data.attributes.name) {
          this.appHeader = analysisResponse.data.attributes.name;

          const appTitle = this.appHeader.startsWith('Polly') ? this.appHeader.slice(6).trim() : this.appHeader;
          this.appHeader = insertAfterFirstWord(appTitle, this.getDatasetId());
          this.titleService.setTitle(`Polly™ ${appTitle}`);
        } else {
          this.appHeader = 'Application Details';
        }

        if (
          Array.isArray(appResponse.data) &&
          appResponse.data[0] &&
          appResponse.data[0].attributes &&
          appResponse.data[0].attributes.app_type &&
          typeof appResponse.data[0].attributes.app_type === 'string'
        ) {
          this.isShinyApp = appResponse.data[0].attributes.app_type.includes(ApplicationType.SHINY);
        } else {
          this.isShinyApp = false;
        }
      },
      error: (error: HttpErrorResponse) => {
        const errorDetail = extractErrorDetails(error, 'Failure', 'Something went wrong while fetching application information');
        this.showCustomToast(errorDetail.title, errorDetail.detail);
      },
    });
  }

  public closeTheRunningInstance(): void {
    if (!this.workspaceId || !this.analysisId || !this.sessionId) return;
    this.tabCloseService.closeApplicationRunningInstance();
  }

  /**
   * This function fetches the DatasetId from splitting the url
   */
  private getDatasetId(): string {
    let datasetId: string = '';
    try {
      const url = new URL(this.appUrl || this.originalAppUrl);
      datasetId = url.searchParams.get('dataset_id') || '';
    } catch (_) {}
    return datasetId;
  }

  private showCustomToast(summary: string, detail: string): void {
    this.messageService.showCustomToast({ severity: 'error', summary, detail, closable: false, });
  }
  /**
   * This function is called when the component is destroyed
   * It closes the running instance of the application
   */
  public ngOnDestroy(): void {
    this.subSink.unsubscribe();
    this.tabCloseService.removeListeners();
  }
}
